/**
 * feat-305 (Addendum 2 §5) — the conversation as plain text, for the
 * "Copy conversation" control.
 *
 *   You: <question>
 *
 *   Assistant: <answer, markdown flattened, markers as [1]>
 *   Shifts cited: 1. Lunch shift, Oct 1; 2. Dinner shift, Oct 2
 *
 * Stopped answers end "(stopped)"; errored/blocked answers end
 * "(didn't finish)". No ids, statuses or timestamps leave this function.
 */
import type { AssistantMessage, Footnote } from './types';
import { finalizeStoppedBody, markdownToPlainText } from './assistantMarkdown';
import { ASSISTANT_COPY } from './copy';

/** An assistant message is "finished" for copy purposes (control visibility). */
export function isCopyableAnswer(m: AssistantMessage): boolean {
  if (m.role !== 'assistant') return false;
  if (m.status === 'complete') return true;
  if (m.status === 'stopped' || m.status === 'error' || m.status === 'blocked') return m.body.trim() !== '';
  return false;
}

function citedLine(footnotes: Footnote[]): string | null {
  if (!footnotes.length) return null;
  const sorted = [...footnotes].sort((a, b) => a.n - b.n);
  return `${ASSISTANT_COPY.shiftsCited}: ${sorted.map((f) => `${f.n}. ${f.label}`).join('; ')}`;
}

function answerBlock(m: AssistantMessage): string {
  const failed = m.status === 'error' || m.status === 'blocked';
  const stopped = m.status === 'stopped';
  const body = stopped || failed ? finalizeStoppedBody(m.body) : m.body;
  const text = markdownToPlainText(body, m.footnotes, { markers: 'bracket', keepLines: true });
  const tag = stopped ? ASSISTANT_COPY.plainStopped : failed ? ASSISTANT_COPY.plainDidNotFinish : '';
  const main = [text, tag].filter(Boolean).join(' ');
  const lines = [`${ASSISTANT_COPY.plainAssistant}: ${main}`.trimEnd()];
  const cited = citedLine(m.footnotes);
  if (cited) lines.push(cited);
  return lines.join('\n');
}

export function conversationToPlainText(messages: AssistantMessage[]): string {
  const blocks: string[] = [];
  for (const m of messages) {
    if (m.role === 'user') {
      blocks.push(`${ASSISTANT_COPY.plainYou}: ${m.body.trim()}`);
    } else if (m.status !== 'streaming') {
      blocks.push(answerBlock(m));
    }
  }
  return blocks.join('\n\n');
}
